import { addDays, format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { useState } from "react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface DatePickerWithPresetsProps {
  date?: Date;
  onDateChange: (date: Date | undefined) => void;
  className?: string;
  showPresets?: boolean;
  showCalendarIcon?: boolean;
  disabledDates?: Date[];
  maxDate?: Date;
  disablePastDates?: boolean;
  modal?: boolean;
}

export function DatePickerWithPresets({
  date,
  onDateChange,
  className,
  showPresets = true,
  showCalendarIcon = false,
  disabledDates = [],
  maxDate,
  disablePastDates = true,
  modal = false,
}: DatePickerWithPresetsProps) {
  const [open, setOpen] = useState(false);

  const handleSelect = (date: Date | undefined) => {
    onDateChange(date);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={modal}>
      <PopoverTrigger asChild>
        <Button
          variant={"outline"}
          className={cn(
            "w-[280px] justify-start text-left font-normal",
            !date && "text-muted-foreground",
            className,
          )}
        >
          {showCalendarIcon && <CalendarIcon className="mr-2 h-5 w-5" />}
          {date ? format(date, "PPP") : <span>Pick a date</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="z-[51] flex w-auto flex-col space-y-2 p-2">
        {showPresets && (
          <Select
            onValueChange={(value) =>
              handleSelect(addDays(new Date(), parseInt(value)))
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent position="popper">
              <SelectItem value="0">Today</SelectItem>
              <SelectItem value="1">Tomorrow</SelectItem>
              <SelectItem value="3">In 3 days</SelectItem>
              <SelectItem value="7">In a week</SelectItem>
            </SelectContent>
          </Select>
        )}
        <div className="rounded-md">
          <Calendar
            mode="single"
            selected={date}
            onSelect={handleSelect}
            className="p-0"
            disabled={(date) => {
              if (maxDate && date > maxDate) {
                return true;
              }

              const yesterday = new Date();
              yesterday.setDate(yesterday.getDate() - 1);

              if (disabledDates.length) {
                return disabledDates.some(
                  (d) => format(d, "yyyy-MM-dd") === format(date, "yyyy-MM-dd"),
                );
              }

              return disablePastDates ? date < yesterday : false;
            }}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
